import {
  SEGMENT_FIELD_TYPES,
  SEGMENT_TYPES,
  URL_MAX_LENGTH,
  IMAGE_POSITION_OPTIONS,
  HIGHLIGHT_ICON_OPTIONS,
  field,
  visualStyleProperty,
  commonSegment
} from "./segmentRegistryCore";
import { IMAGE_CAROUSEL_VISUAL_DEFAULTS, TESTIMONIALS_VISUAL_DEFAULTS } from "../sectionVisualOwnership";
import { HIGHLIGHT_VISUAL_DEFAULTS } from "../highlightVisualOwnership";

const visualColor = (path, label, defaultValue) => field(path, SEGMENT_FIELD_TYPES.COLOR, { label, defaultValue, explicitVisual: true, uiGroup: "design", maxLength: 100 });
const visualNumber = (path, label, defaultValue, min, max, step = 1) => field(path, SEGMENT_FIELD_TYPES.NUMBER, { label, defaultValue, explicitVisual: true, uiGroup: "design", limits: { min, max, step } });
const visualText = (path, label, defaultValue = "none", maxLength = 300) => field(path, SEGMENT_FIELD_TYPES.TEXT, { label, defaultValue, explicitVisual: true, uiGroup: "design", maxLength });
const textStyleFields = (prefix, label, defaults, { responsive = false, maxWidth = false } = {}) => [
  visualColor(`${prefix}.color`, `${label} · color`, defaults.color),
  ...(responsive
    ? [visualNumber(`${prefix}.fontSizeDesktop`, `${label} · tamaño desktop`, defaults.fontSizeDesktop, 8, 120), visualNumber(`${prefix}.fontSizeMobile`, `${label} · tamaño mobile`, defaults.fontSizeMobile, 8, 96)]
    : defaults.fontSize !== undefined ? [visualNumber(`${prefix}.fontSize`, `${label} · tamaño`, defaults.fontSize, 8, 96)] : []),
  ...(defaults.fontWeight !== undefined ? [visualNumber(`${prefix}.fontWeight`, `${label} · peso`, defaults.fontWeight, 100, 900, 100)] : []),
  ...(defaults.lineHeight !== undefined ? [visualNumber(`${prefix}.lineHeight`, `${label} · interlineado`, defaults.lineHeight, .6, 4, .05)] : []),
  ...(defaults.letterSpacing !== undefined ? [visualNumber(`${prefix}.letterSpacing`, `${label} · tracking`, defaults.letterSpacing, -8, 24, .1)] : []),
  ...(maxWidth && defaults.maxWidth !== undefined ? [visualNumber(`${prefix}.maxWidth`, `${label} · ancho máximo`, defaults.maxWidth, 120, 1800, 10)] : []),
];

const contentSegments = {
  [SEGMENT_TYPES.ABOUT]: commonSegment({
    id: SEGMENT_TYPES.ABOUT,
    visibilityPath: "sections.showAbout",
    elementEditingReady: true,
    elementTargets: {
      "about.imageUrl": {
        kind: "image",
        label: "Imagen del negocio",
        fieldPath: "about.imageUrl",
        stylePrefix: "about.imageStyle.",
      },
      "about.ctaLabel": {
        kind: "button",
        label: "Botón",
        labelPath: "about.ctaLabel",
        urlPath: "about.ctaUrl",
        stylePrefix: "about.ctaStyle.",
        extraPaths: ["about.ctaRequiresProducts"],
      },
    },
    shellPropertyPaths: [
      "about.layout",
      "about.backgroundColor",
      "about.contentBackgroundColor",
      "about.contentBorderRadius",
      "about.descriptionToggleColor",
    ],
    shellVisualBackground: false,
    shellVisualPalette: false,
    properties: [
      field("sections.showAbout", SEGMENT_FIELD_TYPES.BOOLEAN, {
        label: "Mostrar sección",
      }),
      field("about.eyebrow", SEGMENT_FIELD_TYPES.TEXT, {
        label: "Texto superior",
        colorPath: "about.eyebrowColor",
        maxLength: 180,
      }),
      field("about.title", SEGMENT_FIELD_TYPES.TEXT, {
        label: "Título",
        colorPath: "about.titleColor",
        maxLength: 220,
      }),
      field("about.description", SEGMENT_FIELD_TYPES.TEXTAREA, {
        label: "Historia o descripción",
        colorPath: "about.descriptionColor",
        maxLength: 2500,
      }),
      field("about.imageUrl", SEGMENT_FIELD_TYPES.IMAGE, {
        label: "Imagen del negocio",
        maxLength: URL_MAX_LENGTH,
        altPath: "about.imageAlt",
        positionPath: "about.imagePosition",
      }),
      field("about.imageAlt", SEGMENT_FIELD_TYPES.TEXT, {
        label: "Texto alternativo de la imagen",
        maxLength: 220,
      }),
      field("about.layout", SEGMENT_FIELD_TYPES.SELECT, {
        label: "Distribución",
        options: [
          { value: "image-left", label: "Imagen a la izquierda" },
          { value: "image-right", label: "Imagen a la derecha" },
        ],
      }),
      field("about.imagePosition", SEGMENT_FIELD_TYPES.SELECT, {
        label: "Posición de la imagen",
        options: IMAGE_POSITION_OPTIONS,
      }),
      field("about.ctaLabel", SEGMENT_FIELD_TYPES.TEXT, {
        label: "Texto del botón",
        maxLength: 100,
      }),
      field("about.ctaUrl", SEGMENT_FIELD_TYPES.URL, {
        label: "Destino del botón",
        maxLength: URL_MAX_LENGTH,
        destinationPicker: true,
      }),
      field("about.ctaRequiresProducts", SEGMENT_FIELD_TYPES.BOOLEAN, {
        label: "El botón requiere catálogo",
      }),
      field("about.backgroundColor", SEGMENT_FIELD_TYPES.COLOR, { label: "Fondo de la sección", defaultValue: "#EEF0F5", explicitVisual: true, maxLength: 100 }),
      field("about.contentBackgroundColor", SEGMENT_FIELD_TYPES.COLOR, { label: "Fondo del bloque de contenido", defaultValue: "transparent", explicitVisual: true, maxLength: 100 }),
      field("about.contentBorderRadius", SEGMENT_FIELD_TYPES.NUMBER, { label: "Redondeado del bloque de contenido (px)", defaultValue: 40, explicitVisual: true, uiGroup: "design", limits: { min: 0, max: 320, step: 1 } }),
      field("about.eyebrowColor", SEGMENT_FIELD_TYPES.COLOR, { label: "Color del texto superior", defaultValue: "#F35A3A", explicitVisual: true, maxLength: 100 }),
      field("about.titleColor", SEGMENT_FIELD_TYPES.COLOR, { label: "Color del título", defaultValue: "#19242F", explicitVisual: true, maxLength: 100 }),
      field("about.descriptionColor", SEGMENT_FIELD_TYPES.COLOR, { label: "Color de la descripción", defaultValue: "#5B5D73", explicitVisual: true, maxLength: 100 }),
      field("about.descriptionToggleColor", SEGMENT_FIELD_TYPES.COLOR, { label: "Color de Ver más / Ver menos", defaultValue: "#F35A3A", explicitVisual: true, maxLength: 100 }),
      field("about.imageStyle.backgroundColor", SEGMENT_FIELD_TYPES.COLOR, { label: "Imagen · fondo/fallback", defaultValue: "rgba(25,36,47,.06)", explicitVisual: true, maxLength: 100, uiGroup: "design" }),
      field("about.imageStyle.borderRadius", SEGMENT_FIELD_TYPES.NUMBER, { label: "Imagen · redondeado (px)", defaultValue: 50, explicitVisual: true, uiGroup: "design", limits: { min: 0, max: 500, step: 1 } }),
      field("about.imageStyle.shadowColor", SEGMENT_FIELD_TYPES.COLOR, { label: "Imagen · color de sombra", defaultValue: "#000000", explicitVisual: true, maxLength: 100, uiGroup: "design" }),
      field("about.imageStyle.shadowOpacity", SEGMENT_FIELD_TYPES.NUMBER, { label: "Imagen · opacidad de sombra", defaultValue: 0.15, explicitVisual: true, uiGroup: "design", limits: { min: 0, max: 1, step: 0.05 } }),
      field("about.imageStyle.shadowX", SEGMENT_FIELD_TYPES.NUMBER, { label: "Imagen · sombra X", defaultValue: 0, explicitVisual: true, uiGroup: "design", limits: { min: -100, max: 100, step: 1 } }),
      field("about.imageStyle.shadowY", SEGMENT_FIELD_TYPES.NUMBER, { label: "Imagen · sombra Y", defaultValue: 24, explicitVisual: true, uiGroup: "design", limits: { min: -100, max: 100, step: 1 } }),
      field("about.imageStyle.shadowBlur", SEGMENT_FIELD_TYPES.NUMBER, { label: "Imagen · desenfoque de sombra", defaultValue: 70, explicitVisual: true, uiGroup: "design", limits: { min: 0, max: 200, step: 1 } }),
      field("about.imageStyle.shadowSpread", SEGMENT_FIELD_TYPES.NUMBER, { label: "Imagen · expansión de sombra", defaultValue: 0, explicitVisual: true, uiGroup: "design", limits: { min: -80, max: 80, step: 1 } }),
      field("about.ctaStyle.backgroundColor", SEGMENT_FIELD_TYPES.COLOR, { label: "Botón · fondo", defaultValue: "#F35A3A", explicitVisual: true, maxLength: 100 }),
      field("about.ctaStyle.textColor", SEGMENT_FIELD_TYPES.COLOR, { label: "Botón · texto", defaultValue: "#FFFFFF", explicitVisual: true, maxLength: 100 }),
      field("about.ctaStyle.hoverBackgroundColor", SEGMENT_FIELD_TYPES.COLOR, { label: "Botón · fondo hover", defaultValue: "#E05335", explicitVisual: true, maxLength: 100 }),
      field("about.ctaStyle.hoverTextColor", SEGMENT_FIELD_TYPES.COLOR, { label: "Botón · texto hover", defaultValue: "#EBEBEB", explicitVisual: true, maxLength: 100 }),
      field("about.ctaStyle.borderColor", SEGMENT_FIELD_TYPES.COLOR, { label: "Botón · borde", defaultValue: "transparent", explicitVisual: true, maxLength: 100 }),
      field("about.ctaStyle.borderWidth", SEGMENT_FIELD_TYPES.NUMBER, { label: "Botón · grosor de borde", defaultValue: 0, explicitVisual: true, limits: { min: 0, max: 16, step: 1 } }),
      field("about.ctaStyle.borderRadius", SEGMENT_FIELD_TYPES.NUMBER, { label: "Botón · redondeado", defaultValue: 999, explicitVisual: true, limits: { min: 0, max: 999, step: 1 } }),
      field("about.ctaStyle.shadowColor", SEGMENT_FIELD_TYPES.COLOR, { label: "Botón · sombra", defaultValue: "#141628", explicitVisual: true, maxLength: 100 }),
      field("about.ctaStyle.shadowOpacity", SEGMENT_FIELD_TYPES.NUMBER, { label: "Botón · opacidad de sombra", defaultValue: 0.14, explicitVisual: true, limits: { min: 0, max: 1, step: 0.05 } }),
      field("about.ctaStyle.shadowX", SEGMENT_FIELD_TYPES.NUMBER, { label: "Botón · sombra X", defaultValue: 0, explicitVisual: true, limits: { min: -100, max: 100, step: 1 } }),
      field("about.ctaStyle.shadowY", SEGMENT_FIELD_TYPES.NUMBER, { label: "Botón · sombra Y", defaultValue: 8, explicitVisual: true, limits: { min: -100, max: 100, step: 1 } }),
      field("about.ctaStyle.shadowBlur", SEGMENT_FIELD_TYPES.NUMBER, { label: "Botón · desenfoque de sombra", defaultValue: 20, explicitVisual: true, limits: { min: 0, max: 160, step: 1 } }),
      field("about.ctaStyle.shadowSpread", SEGMENT_FIELD_TYPES.NUMBER, { label: "Botón · expansión de sombra", defaultValue: 0, explicitVisual: true, limits: { min: -80, max: 80, step: 1 } }),
      field("about.ctaStyle.hoverShadowColor", SEGMENT_FIELD_TYPES.COLOR, { label: "Botón · sombra hover", defaultValue: "#141628", explicitVisual: true, maxLength: 100 }),
      field("about.ctaStyle.hoverShadowOpacity", SEGMENT_FIELD_TYPES.NUMBER, { label: "Botón · opacidad sombra hover", defaultValue: 0.2, explicitVisual: true, limits: { min: 0, max: 1, step: 0.05 } }),
      field("about.ctaStyle.hoverShadowX", SEGMENT_FIELD_TYPES.NUMBER, { label: "Botón · sombra hover X", defaultValue: 0, explicitVisual: true, limits: { min: -100, max: 100, step: 1 } }),
      field("about.ctaStyle.hoverShadowY", SEGMENT_FIELD_TYPES.NUMBER, { label: "Botón · sombra hover Y", defaultValue: 12, explicitVisual: true, limits: { min: -100, max: 100, step: 1 } }),
      field("about.ctaStyle.hoverShadowBlur", SEGMENT_FIELD_TYPES.NUMBER, { label: "Botón · desenfoque sombra hover", defaultValue: 28, explicitVisual: true, limits: { min: 0, max: 160, step: 1 } }),
      field("about.ctaStyle.hoverShadowSpread", SEGMENT_FIELD_TYPES.NUMBER, { label: "Botón · expansión sombra hover", defaultValue: 0, explicitVisual: true, limits: { min: -80, max: 80, step: 1 } }),
      field("about.ctaStyle.focusRingColor", SEGMENT_FIELD_TYPES.COLOR, { label: "Botón · foco visible", defaultValue: "#F35A3A18", explicitVisual: true, maxLength: 100 }),
      visualStyleProperty(SEGMENT_TYPES.ABOUT),
    ],
  }),
  [SEGMENT_TYPES.HIGHLIGHTS]: commonSegment({
    id: SEGMENT_TYPES.HIGHLIGHTS,
    visibilityPath: "sections.showHighlights",
    elementEditingReady: true,
    elementTargets: {
      "@highlights.collection": {
        kind: "collection-manager",
        label: "Diferenciales",
        collectionPath: "highlights",
        editorMode: "highlights",
      },
    },
    shellPropertyPaths: [
      "highlightsIntro.backgroundImageUrl",
      "highlightsIntro.backgroundImagePosition",
      "highlightsIntro.backgroundImageOpacity",
      "highlightsIntro.backgroundColor",
    ],
    shellVisualBackground: false,
    shellVisualPalette: false,
    properties: [
      field("sections.showHighlights", SEGMENT_FIELD_TYPES.BOOLEAN, {
        label: "Mostrar diferenciales",
      }),
      field("highlightsIntro.eyebrow", SEGMENT_FIELD_TYPES.TEXT, {
        label: "Texto superior",
        colorPath: "highlightsIntro.eyebrowColor",
        maxLength: 180,
      }),
      field("highlightsIntro.title", SEGMENT_FIELD_TYPES.TEXT, {
        label: "Título",
        colorPath: "highlightsIntro.titleColor",
        maxLength: 220,
      }),
      field("highlightsIntro.subtitle", SEGMENT_FIELD_TYPES.TEXTAREA, {
        label: "Descripción",
        colorPath: "highlightsIntro.subtitleColor",
        maxLength: 900,
      }),
      field("highlights", SEGMENT_FIELD_TYPES.COLLECTION, {
        label: "Diferenciales",
        limits: { maxItems: 24 },
        item: {
          fields: [
            { key: "id", type: SEGMENT_FIELD_TYPES.TEXT, maxLength: 100 },
            { key: "icon", type: SEGMENT_FIELD_TYPES.SELECT, maxLength: 50, options: HIGHLIGHT_ICON_OPTIONS },
            { key: "title", type: SEGMENT_FIELD_TYPES.TEXT, maxLength: 160, colorKey: "titleStyle.color", styleKey: "titleStyle" },
            { key: "description", type: SEGMENT_FIELD_TYPES.TEXTAREA, maxLength: 600, colorKey: "descriptionStyle.color", styleKey: "descriptionStyle" },
            { key: "imageUrl", label: "Imagen de la tarjeta", type: SEGMENT_FIELD_TYPES.IMAGE, maxLength: URL_MAX_LENGTH, styleKey: "imageStyle" },
            { key: "imageAlt", type: SEGMENT_FIELD_TYPES.TEXT, maxLength: 220 },
            { key: "imagePosition", label: "Posición de la imagen", type: SEGMENT_FIELD_TYPES.SELECT, options: IMAGE_POSITION_OPTIONS },
            { key: "youtubeUrl", label: "Video de YouTube", type: SEGMENT_FIELD_TYPES.URL, maxLength: URL_MAX_LENGTH, styleKey: "videoStyle" },
            { key: "cardStyle.backgroundColor", label: "Card · fondo", type: SEGMENT_FIELD_TYPES.COLOR, defaultValue: HIGHLIGHT_VISUAL_DEFAULTS.cardStyle.backgroundColor, explicitVisual: true, maxLength: 100 },
            { key: "cardStyle.borderColor", label: "Card · borde", type: SEGMENT_FIELD_TYPES.COLOR, defaultValue: HIGHLIGHT_VISUAL_DEFAULTS.cardStyle.borderColor, explicitVisual: true, maxLength: 100 },
            { key: "cardStyle.borderWidth", label: "Card · ancho borde", type: SEGMENT_FIELD_TYPES.NUMBER, defaultValue: HIGHLIGHT_VISUAL_DEFAULTS.cardStyle.borderWidth, limits: { min: 0, max: 16, step: 1 } },
            { key: "cardStyle.borderRadius", label: "Card · radio", type: SEGMENT_FIELD_TYPES.NUMBER, defaultValue: HIGHLIGHT_VISUAL_DEFAULTS.cardStyle.borderRadius, limits: { min: 0, max: 160, step: 1 } },
            { key: "cardStyle.boxShadow", label: "Card · sombra", type: SEGMENT_FIELD_TYPES.TEXT, defaultValue: HIGHLIGHT_VISUAL_DEFAULTS.cardStyle.boxShadow, maxLength: 300 },
            { key: "cardStyle.hoverBoxShadow", label: "Card · sombra hover", type: SEGMENT_FIELD_TYPES.TEXT, defaultValue: HIGHLIGHT_VISUAL_DEFAULTS.cardStyle.hoverBoxShadow, maxLength: 300 },
            { key: "cardStyle.hoverTranslateY", label: "Card · desplazamiento hover", type: SEGMENT_FIELD_TYPES.NUMBER, defaultValue: HIGHLIGHT_VISUAL_DEFAULTS.cardStyle.hoverTranslateY, limits: { min: -32, max: 32, step: 1 } },
            { key: "cardStyle.contentPadding", label: "Card · padding", type: SEGMENT_FIELD_TYPES.NUMBER, defaultValue: HIGHLIGHT_VISUAL_DEFAULTS.cardStyle.contentPadding, limits: { min: 0, max: 96, step: 1 } },
            { key: "iconStyle.color", label: "Ícono · color", type: SEGMENT_FIELD_TYPES.COLOR, defaultValue: HIGHLIGHT_VISUAL_DEFAULTS.iconStyle.color, explicitVisual: true, maxLength: 100 },
            { key: "iconStyle.size", label: "Ícono · tamaño", type: SEGMENT_FIELD_TYPES.NUMBER, defaultValue: HIGHLIGHT_VISUAL_DEFAULTS.iconStyle.size, limits: { min: 8, max: 96, step: 1 } },
            { key: "iconSurfaceStyle.backgroundColor", label: "Superficie del ícono · fondo", type: SEGMENT_FIELD_TYPES.COLOR, defaultValue: HIGHLIGHT_VISUAL_DEFAULTS.iconSurfaceStyle.backgroundColor, explicitVisual: true, maxLength: 100 },
            { key: "iconSurfaceStyle.floatingBackgroundColor", label: "Superficie flotante · fondo", type: SEGMENT_FIELD_TYPES.COLOR, defaultValue: HIGHLIGHT_VISUAL_DEFAULTS.iconSurfaceStyle.floatingBackgroundColor, explicitVisual: true, maxLength: 100 },
            { key: "iconSurfaceStyle.borderColor", label: "Superficie del ícono · borde", type: SEGMENT_FIELD_TYPES.COLOR, defaultValue: HIGHLIGHT_VISUAL_DEFAULTS.iconSurfaceStyle.borderColor, explicitVisual: true, maxLength: 100 },
            { key: "iconSurfaceStyle.borderWidth", label: "Superficie del ícono · ancho borde", type: SEGMENT_FIELD_TYPES.NUMBER, defaultValue: HIGHLIGHT_VISUAL_DEFAULTS.iconSurfaceStyle.borderWidth, limits: { min: 0, max: 16, step: 1 } },
            { key: "iconSurfaceStyle.borderRadius", label: "Superficie del ícono · radio", type: SEGMENT_FIELD_TYPES.NUMBER, defaultValue: HIGHLIGHT_VISUAL_DEFAULTS.iconSurfaceStyle.borderRadius, limits: { min: 0, max: 160, step: 1 } },
            { key: "iconSurfaceStyle.width", label: "Superficie del ícono · ancho", type: SEGMENT_FIELD_TYPES.NUMBER, defaultValue: HIGHLIGHT_VISUAL_DEFAULTS.iconSurfaceStyle.width, limits: { min: 20, max: 160, step: 1 } },
            { key: "iconSurfaceStyle.height", label: "Superficie del ícono · alto", type: SEGMENT_FIELD_TYPES.NUMBER, defaultValue: HIGHLIGHT_VISUAL_DEFAULTS.iconSurfaceStyle.height, limits: { min: 20, max: 160, step: 1 } },
            { key: "iconSurfaceStyle.boxShadow", label: "Superficie del ícono · sombra", type: SEGMENT_FIELD_TYPES.TEXT, defaultValue: HIGHLIGHT_VISUAL_DEFAULTS.iconSurfaceStyle.boxShadow, maxLength: 300 },
            { key: "iconSurfaceStyle.floatingBoxShadow", label: "Superficie flotante · sombra", type: SEGMENT_FIELD_TYPES.TEXT, defaultValue: HIGHLIGHT_VISUAL_DEFAULTS.iconSurfaceStyle.floatingBoxShadow, maxLength: 300 },
            { key: "titleStyle.color", label: "Título · color", type: SEGMENT_FIELD_TYPES.COLOR, defaultValue: HIGHLIGHT_VISUAL_DEFAULTS.titleStyle.color, explicitVisual: true, maxLength: 100 },
            { key: "titleStyle.fontSize", label: "Título · tamaño", type: SEGMENT_FIELD_TYPES.NUMBER, defaultValue: HIGHLIGHT_VISUAL_DEFAULTS.titleStyle.fontSize, limits: { min: 8, max: 96, step: 1 } },
            { key: "titleStyle.fontWeight", label: "Título · peso", type: SEGMENT_FIELD_TYPES.NUMBER, defaultValue: HIGHLIGHT_VISUAL_DEFAULTS.titleStyle.fontWeight, limits: { min: 100, max: 900, step: 100 } },
            { key: "titleStyle.lineHeight", label: "Título · interlineado", type: SEGMENT_FIELD_TYPES.NUMBER, defaultValue: HIGHLIGHT_VISUAL_DEFAULTS.titleStyle.lineHeight, limits: { min: .6, max: 4, step: .05 } },
            { key: "descriptionStyle.color", label: "Descripción · color", type: SEGMENT_FIELD_TYPES.COLOR, defaultValue: HIGHLIGHT_VISUAL_DEFAULTS.descriptionStyle.color, explicitVisual: true, maxLength: 100 },
            { key: "descriptionStyle.fontSize", label: "Descripción · tamaño", type: SEGMENT_FIELD_TYPES.NUMBER, defaultValue: HIGHLIGHT_VISUAL_DEFAULTS.descriptionStyle.fontSize, limits: { min: 8, max: 96, step: 1 } },
            { key: "descriptionStyle.fontWeight", label: "Descripción · peso", type: SEGMENT_FIELD_TYPES.NUMBER, defaultValue: HIGHLIGHT_VISUAL_DEFAULTS.descriptionStyle.fontWeight, limits: { min: 100, max: 900, step: 100 } },
            { key: "descriptionStyle.lineHeight", label: "Descripción · interlineado", type: SEGMENT_FIELD_TYPES.NUMBER, defaultValue: HIGHLIGHT_VISUAL_DEFAULTS.descriptionStyle.lineHeight, limits: { min: .6, max: 4, step: .05 } },
            { key: "imageStyle.backgroundColor", label: "Imagen · superficie", type: SEGMENT_FIELD_TYPES.COLOR, defaultValue: HIGHLIGHT_VISUAL_DEFAULTS.imageStyle.backgroundColor, explicitVisual: true, maxLength: 100 },
            { key: "imageStyle.objectFit", label: "Imagen · ajuste", type: SEGMENT_FIELD_TYPES.SELECT, defaultValue: HIGHLIGHT_VISUAL_DEFAULTS.imageStyle.objectFit, options: [{ value: "cover", label: "Cubrir" }, { value: "contain", label: "Contener" }] },
            { key: "imageStyle.height", label: "Imagen · alto", type: SEGMENT_FIELD_TYPES.NUMBER, defaultValue: HIGHLIGHT_VISUAL_DEFAULTS.imageStyle.height, limits: { min: 80, max: 640, step: 10 } },
            { key: "videoStyle.backgroundColor", label: "Video · superficie", type: SEGMENT_FIELD_TYPES.COLOR, defaultValue: HIGHLIGHT_VISUAL_DEFAULTS.videoStyle.backgroundColor, explicitVisual: true, maxLength: 100 },
            { key: "videoStyle.aspectRatio", label: "Video · proporción", type: SEGMENT_FIELD_TYPES.SELECT, defaultValue: HIGHLIGHT_VISUAL_DEFAULTS.videoStyle.aspectRatio, options: [{ value: "16 / 9", label: "16:9" }, { value: "4 / 3", label: "4:3" }, { value: "1 / 1", label: "1:1" }] },
          ],
        },
      }),
      field("highlightsIntro.backgroundImageUrl", SEGMENT_FIELD_TYPES.IMAGE, {
        label: "Foto de fondo opcional",
        maxLength: URL_MAX_LENGTH,
        positionPath: "highlightsIntro.backgroundImagePosition",
      }),
      field("highlightsIntro.backgroundImagePosition", SEGMENT_FIELD_TYPES.SELECT, {
        label: "Posición de la foto de fondo", options: IMAGE_POSITION_OPTIONS,
      }),
      field("highlightsIntro.backgroundImageOpacity", SEGMENT_FIELD_TYPES.NUMBER, {
        label: "Intensidad de la foto de fondo", min: 0, max: 1, step: 0.05, defaultValue: 0.24,
      }),
      field("highlightsIntro.backgroundColor", SEGMENT_FIELD_TYPES.COLOR, { label: "Fondo de la sección", defaultValue: "#FFFFFF", explicitVisual: true, maxLength: 100 }),
      field("highlightsIntro.eyebrowColor", SEGMENT_FIELD_TYPES.COLOR, { label: "Color del texto superior", defaultValue: "#F35A3A", explicitVisual: true, maxLength: 100 }),
      field("highlightsIntro.titleColor", SEGMENT_FIELD_TYPES.COLOR, { label: "Color del título", defaultValue: "#19242F", explicitVisual: true, maxLength: 100 }),
      field("highlightsIntro.subtitleColor", SEGMENT_FIELD_TYPES.COLOR, { label: "Color de la descripción", defaultValue: "#5B5D73", explicitVisual: true, maxLength: 100 }),
      visualStyleProperty(SEGMENT_TYPES.HIGHLIGHTS),
    ],
  }),
  [SEGMENT_TYPES.IMAGE_CAROUSEL]: commonSegment({
    id: SEGMENT_TYPES.IMAGE_CAROUSEL,
    visibilityPath: "sections.showImageCarousel",
    elementEditingReady: true,
    elementTargets: {
      "imageCarousel.eyebrow": { kind: "text", label: "Texto superior", fieldPath: "imageCarousel.eyebrow", colorPath: "imageCarousel.eyebrowStyle.color", stylePrefix: "imageCarousel.eyebrowStyle." },
      "imageCarousel.title": { kind: "text", label: "Título", fieldPath: "imageCarousel.title", colorPath: "imageCarousel.titleStyle.color", stylePrefix: "imageCarousel.titleStyle." },
      "imageCarousel.subtitle": { kind: "text", label: "Descripción", fieldPath: "imageCarousel.subtitle", colorPath: "imageCarousel.subtitleStyle.color", stylePrefix: "imageCarousel.subtitleStyle." },
      "imageCarousel.items": { kind: "collection-manager", label: "Imágenes del carrusel", collectionPath: "imageCarousel.items", editorMode: "image-carousel" },
    },
    shellPropertyPaths: [
      "imageCarousel.shellStyle.backgroundColor",
      "imageCarousel.autoplay", "imageCarousel.intervalMs", "imageCarousel.showArrows", "imageCarousel.showDots",
    ],
    shellAdvancedGroups: [
      {
        label: "Imagen",
        paths: [
          "imageCarousel.mediaStyle.backgroundColor", "imageCarousel.mediaStyle.borderColor", "imageCarousel.mediaStyle.borderWidth", "imageCarousel.mediaStyle.borderRadius", "imageCarousel.mediaStyle.boxShadow", "imageCarousel.mediaStyle.heightDesktop", "imageCarousel.mediaStyle.heightMobile",
        ],
      },
      {
        label: "Flechas",
        paths: [
          "imageCarousel.arrowStyle.color", "imageCarousel.arrowStyle.backgroundColor", "imageCarousel.arrowStyle.borderColor", "imageCarousel.arrowStyle.hoverColor", "imageCarousel.arrowStyle.hoverBackgroundColor", "imageCarousel.arrowStyle.hoverBorderColor", "imageCarousel.arrowStyle.focusColor", "imageCarousel.arrowStyle.size", "imageCarousel.arrowStyle.boxShadow",
        ],
      },
      {
        label: "Indicadores",
        paths: [
          "imageCarousel.dotStyle.color", "imageCarousel.dotStyle.activeColor", "imageCarousel.dotStyle.hoverColor", "imageCarousel.dotStyle.activeHoverColor", "imageCarousel.dotStyle.height", "imageCarousel.dotStyle.width", "imageCarousel.dotStyle.activeWidth",
        ],
      },
    ],
    shellVisualBackground: false,
    shellVisualPalette: false,
    properties: [
      field("sections.showImageCarousel", SEGMENT_FIELD_TYPES.BOOLEAN, { label: "Mostrar carrusel" }),
      field("imageCarousel.eyebrow", SEGMENT_FIELD_TYPES.TEXT, { label: "Texto superior", colorPath: "imageCarousel.eyebrowStyle.color", stylePrefix: "imageCarousel.eyebrowStyle.", maxLength: 180 }),
      field("imageCarousel.title", SEGMENT_FIELD_TYPES.TEXT, { label: "Título", colorPath: "imageCarousel.titleStyle.color", stylePrefix: "imageCarousel.titleStyle.", maxLength: 220 }),
      field("imageCarousel.subtitle", SEGMENT_FIELD_TYPES.TEXTAREA, { label: "Descripción", colorPath: "imageCarousel.subtitleStyle.color", stylePrefix: "imageCarousel.subtitleStyle.", maxLength: 900 }),
      field("imageCarousel.items", SEGMENT_FIELD_TYPES.COLLECTION, {
        label: "Imágenes", limits: { maxItems: 20 },
        item: { fields: [
          { key: "imageUrl", label: "Imagen", type: SEGMENT_FIELD_TYPES.IMAGE, stylePrefix: "imageCarousel.mediaStyle.", maxLength: URL_MAX_LENGTH },
          { key: "imageAlt", label: "Texto alternativo", type: SEGMENT_FIELD_TYPES.TEXT, maxLength: 220 },
          { key: "caption", label: "Epígrafe", type: SEGMENT_FIELD_TYPES.TEXTAREA, colorPath: "imageCarousel.captionStyle.color", stylePrefix: "imageCarousel.captionStyle.", maxLength: 500 },
          { key: "imagePosition", label: "Posición", type: SEGMENT_FIELD_TYPES.SELECT, options: IMAGE_POSITION_OPTIONS },
        ] },
      }),
      field("imageCarousel.autoplay", SEGMENT_FIELD_TYPES.BOOLEAN, { label: "Avanzar automáticamente", uiGroup: "behavior" }),
      field("imageCarousel.intervalMs", SEGMENT_FIELD_TYPES.NUMBER, { label: "Tiempo entre imágenes (ms)", defaultValue: 4500, uiGroup: "behavior", limits: { min: 2000, max: 15000, step: 500 } }),
      field("imageCarousel.showArrows", SEGMENT_FIELD_TYPES.BOOLEAN, { label: "Mostrar flechas", uiGroup: "behavior" }),
      field("imageCarousel.showDots", SEGMENT_FIELD_TYPES.BOOLEAN, { label: "Mostrar indicadores", uiGroup: "behavior" }),
      visualColor("imageCarousel.shellStyle.backgroundColor", "Sección · fondo", IMAGE_CAROUSEL_VISUAL_DEFAULTS.shellStyle.backgroundColor),
      ...textStyleFields("imageCarousel.eyebrowStyle", "Texto superior", IMAGE_CAROUSEL_VISUAL_DEFAULTS.eyebrowStyle),
      ...textStyleFields("imageCarousel.titleStyle", "Título", IMAGE_CAROUSEL_VISUAL_DEFAULTS.titleStyle, { responsive: true }),
      ...textStyleFields("imageCarousel.subtitleStyle", "Descripción", IMAGE_CAROUSEL_VISUAL_DEFAULTS.subtitleStyle, { maxWidth: true }),
      visualColor("imageCarousel.mediaStyle.backgroundColor", "Imagen · fondo", IMAGE_CAROUSEL_VISUAL_DEFAULTS.mediaStyle.backgroundColor),
      visualColor("imageCarousel.mediaStyle.borderColor", "Imagen · borde", IMAGE_CAROUSEL_VISUAL_DEFAULTS.mediaStyle.borderColor),
      visualNumber("imageCarousel.mediaStyle.borderWidth", "Imagen · grosor borde", IMAGE_CAROUSEL_VISUAL_DEFAULTS.mediaStyle.borderWidth, 0, 16),
      visualNumber("imageCarousel.mediaStyle.borderRadius", "Imagen · redondeado", IMAGE_CAROUSEL_VISUAL_DEFAULTS.mediaStyle.borderRadius, 0, 160),
      visualText("imageCarousel.mediaStyle.boxShadow", "Imagen · sombra", IMAGE_CAROUSEL_VISUAL_DEFAULTS.mediaStyle.boxShadow),
      visualNumber("imageCarousel.mediaStyle.heightDesktop", "Imagen · alto desktop", IMAGE_CAROUSEL_VISUAL_DEFAULTS.mediaStyle.heightDesktop, 220, 800, 10),
      visualNumber("imageCarousel.mediaStyle.heightMobile", "Imagen · alto mobile", IMAGE_CAROUSEL_VISUAL_DEFAULTS.mediaStyle.heightMobile, 180, 640, 10),
      ...textStyleFields("imageCarousel.captionStyle", "Epígrafe", IMAGE_CAROUSEL_VISUAL_DEFAULTS.captionStyle, { responsive: true, maxWidth: true }),
      visualColor("imageCarousel.captionStyle.overlayColor", "Imagen · overlay", IMAGE_CAROUSEL_VISUAL_DEFAULTS.captionStyle.overlayColor),
      visualColor("imageCarousel.arrowStyle.color", "Flecha · ícono", IMAGE_CAROUSEL_VISUAL_DEFAULTS.arrowStyle.color),
      visualColor("imageCarousel.arrowStyle.backgroundColor", "Flecha · fondo", IMAGE_CAROUSEL_VISUAL_DEFAULTS.arrowStyle.backgroundColor),
      visualColor("imageCarousel.arrowStyle.borderColor", "Flecha · borde", IMAGE_CAROUSEL_VISUAL_DEFAULTS.arrowStyle.borderColor),
      visualColor("imageCarousel.arrowStyle.hoverColor", "Flecha hover · ícono", IMAGE_CAROUSEL_VISUAL_DEFAULTS.arrowStyle.hoverColor),
      visualColor("imageCarousel.arrowStyle.hoverBackgroundColor", "Flecha hover · fondo", IMAGE_CAROUSEL_VISUAL_DEFAULTS.arrowStyle.hoverBackgroundColor),
      visualColor("imageCarousel.arrowStyle.hoverBorderColor", "Flecha hover · borde", IMAGE_CAROUSEL_VISUAL_DEFAULTS.arrowStyle.hoverBorderColor),
      visualColor("imageCarousel.arrowStyle.focusColor", "Flecha · foco", IMAGE_CAROUSEL_VISUAL_DEFAULTS.arrowStyle.focusColor),
      visualNumber("imageCarousel.arrowStyle.size", "Flecha · tamaño", IMAGE_CAROUSEL_VISUAL_DEFAULTS.arrowStyle.size, 28, 80),
      visualText("imageCarousel.arrowStyle.boxShadow", "Flecha · sombra", IMAGE_CAROUSEL_VISUAL_DEFAULTS.arrowStyle.boxShadow),
      visualColor("imageCarousel.dotStyle.color", "Indicador · color", IMAGE_CAROUSEL_VISUAL_DEFAULTS.dotStyle.color),
      visualColor("imageCarousel.dotStyle.activeColor", "Indicador activo · color", IMAGE_CAROUSEL_VISUAL_DEFAULTS.dotStyle.activeColor),
      visualColor("imageCarousel.dotStyle.hoverColor", "Indicador hover · color", IMAGE_CAROUSEL_VISUAL_DEFAULTS.dotStyle.hoverColor),
      visualColor("imageCarousel.dotStyle.activeHoverColor", "Indicador activo hover · color", IMAGE_CAROUSEL_VISUAL_DEFAULTS.dotStyle.activeHoverColor),
      visualNumber("imageCarousel.dotStyle.height", "Indicador · alto", IMAGE_CAROUSEL_VISUAL_DEFAULTS.dotStyle.height, 2, 32),
      visualNumber("imageCarousel.dotStyle.width", "Indicador · ancho", IMAGE_CAROUSEL_VISUAL_DEFAULTS.dotStyle.width, 2, 64),
      visualNumber("imageCarousel.dotStyle.activeWidth", "Indicador activo · ancho", IMAGE_CAROUSEL_VISUAL_DEFAULTS.dotStyle.activeWidth, 2, 96),
      visualStyleProperty(SEGMENT_TYPES.IMAGE_CAROUSEL),
    ],
  }),
  [SEGMENT_TYPES.TESTIMONIALS]: commonSegment({
    id: SEGMENT_TYPES.TESTIMONIALS,
    visibilityPath: "sections.showTestimonials",
    elementEditingReady: true,
    elementTargets: {
      "testimonials.eyebrow": { kind: "text", label: "Texto superior", fieldPath: "testimonials.eyebrow", colorPath: "testimonials.eyebrowStyle.color", stylePrefix: "testimonials.eyebrowStyle." },
      "testimonials.title": { kind: "text", label: "Título", fieldPath: "testimonials.title", colorPath: "testimonials.titleStyle.color", stylePrefix: "testimonials.titleStyle." },
      "testimonials.subtitle": { kind: "text", label: "Descripción", fieldPath: "testimonials.subtitle", colorPath: "testimonials.subtitleStyle.color", stylePrefix: "testimonials.subtitleStyle." },
      "testimonials.items": { kind: "collection-manager", label: "Testimonios", collectionPath: "testimonials.items", stylePrefix: "testimonials.cardStyle." },
    },
    shellPropertyPaths: [
      "testimonials.shellStyle.backgroundColor",
    ],
    shellAdvancedGroups: [
      {
        label: "Tarjetas",
        paths: [
          "testimonials.cardStyle.backgroundColor", "testimonials.cardStyle.borderColor", "testimonials.cardStyle.borderWidth", "testimonials.cardStyle.borderRadius", "testimonials.cardStyle.boxShadow", "testimonials.cardStyle.hoverBackgroundColor", "testimonials.cardStyle.hoverBorderColor", "testimonials.cardStyle.hoverBoxShadow", "testimonials.cardStyle.hoverTranslateY", "testimonials.cardStyle.paddingDesktop", "testimonials.cardStyle.paddingMobile",
        ],
      },
      {
        label: "Comillas y avatar",
        paths: [
          "testimonials.accentStyle.color", "testimonials.accentStyle.fontSize", "testimonials.accentStyle.fontWeight", "testimonials.accentStyle.lineHeight",
          "testimonials.avatarStyle.backgroundColor", "testimonials.avatarStyle.color", "testimonials.avatarStyle.size", "testimonials.avatarStyle.borderColor", "testimonials.avatarStyle.borderWidth", "testimonials.avatarStyle.boxShadow",
        ],
      },
    ],
    shellVisualBackground: false,
    shellVisualPalette: false,
    properties: [
      field("sections.showTestimonials", SEGMENT_FIELD_TYPES.BOOLEAN, { label: "Mostrar testimonios" }),
      field("testimonials.eyebrow", SEGMENT_FIELD_TYPES.TEXT, { label: "Texto superior", colorPath: "testimonials.eyebrowStyle.color", stylePrefix: "testimonials.eyebrowStyle.", maxLength: 180 }),
      field("testimonials.title", SEGMENT_FIELD_TYPES.TEXT, { label: "Título", colorPath: "testimonials.titleStyle.color", stylePrefix: "testimonials.titleStyle.", maxLength: 220 }),
      field("testimonials.subtitle", SEGMENT_FIELD_TYPES.TEXTAREA, { label: "Descripción", colorPath: "testimonials.subtitleStyle.color", stylePrefix: "testimonials.subtitleStyle.", maxLength: 900 }),
      field("testimonials.items", SEGMENT_FIELD_TYPES.COLLECTION, {
        label: "Testimonios", limits: { maxItems: 18 },
        item: { fields: [
          { key: "quote", label: "Testimonio", type: SEGMENT_FIELD_TYPES.TEXTAREA, colorPath: "testimonials.quoteStyle.color", stylePrefix: "testimonials.quoteStyle.", maxLength: 1200 },
          { key: "name", label: "Nombre", type: SEGMENT_FIELD_TYPES.TEXT, colorPath: "testimonials.nameStyle.color", stylePrefix: "testimonials.nameStyle.", maxLength: 160 },
          { key: "role", label: "Cargo / referencia", type: SEGMENT_FIELD_TYPES.TEXT, colorPath: "testimonials.roleStyle.color", stylePrefix: "testimonials.roleStyle.", maxLength: 220 },
          { key: "avatarUrl", label: "Foto", type: SEGMENT_FIELD_TYPES.IMAGE, stylePrefix: "testimonials.avatarStyle.", maxLength: URL_MAX_LENGTH },
          { key: "avatarAlt", label: "Texto alternativo", type: SEGMENT_FIELD_TYPES.TEXT, maxLength: 220 },
        ] },
      }),
      visualColor("testimonials.shellStyle.backgroundColor", "Sección · fondo", TESTIMONIALS_VISUAL_DEFAULTS.shellStyle.backgroundColor),
      ...textStyleFields("testimonials.eyebrowStyle", "Texto superior", TESTIMONIALS_VISUAL_DEFAULTS.eyebrowStyle),
      ...textStyleFields("testimonials.titleStyle", "Título", TESTIMONIALS_VISUAL_DEFAULTS.titleStyle, { responsive: true }),
      ...textStyleFields("testimonials.subtitleStyle", "Descripción", TESTIMONIALS_VISUAL_DEFAULTS.subtitleStyle, { maxWidth: true }),
      visualColor("testimonials.cardStyle.backgroundColor", "Tarjeta · fondo", TESTIMONIALS_VISUAL_DEFAULTS.cardStyle.backgroundColor),
      visualColor("testimonials.cardStyle.borderColor", "Tarjeta · borde", TESTIMONIALS_VISUAL_DEFAULTS.cardStyle.borderColor),
      visualNumber("testimonials.cardStyle.borderWidth", "Tarjeta · grosor borde", TESTIMONIALS_VISUAL_DEFAULTS.cardStyle.borderWidth, 0, 16),
      visualNumber("testimonials.cardStyle.borderRadius", "Tarjeta · redondeado", TESTIMONIALS_VISUAL_DEFAULTS.cardStyle.borderRadius, 0, 160),
      visualText("testimonials.cardStyle.boxShadow", "Tarjeta · sombra", TESTIMONIALS_VISUAL_DEFAULTS.cardStyle.boxShadow),
      visualColor("testimonials.cardStyle.hoverBackgroundColor", "Tarjeta hover · fondo", TESTIMONIALS_VISUAL_DEFAULTS.cardStyle.hoverBackgroundColor),
      visualColor("testimonials.cardStyle.hoverBorderColor", "Tarjeta hover · borde", TESTIMONIALS_VISUAL_DEFAULTS.cardStyle.hoverBorderColor),
      visualText("testimonials.cardStyle.hoverBoxShadow", "Tarjeta hover · sombra", TESTIMONIALS_VISUAL_DEFAULTS.cardStyle.hoverBoxShadow),
      visualNumber("testimonials.cardStyle.hoverTranslateY", "Tarjeta hover · desplazamiento Y", TESTIMONIALS_VISUAL_DEFAULTS.cardStyle.hoverTranslateY, -32, 32),
      visualNumber("testimonials.cardStyle.paddingDesktop", "Tarjeta · padding desktop", TESTIMONIALS_VISUAL_DEFAULTS.cardStyle.paddingDesktop, 0, 96),
      visualNumber("testimonials.cardStyle.paddingMobile", "Tarjeta · padding mobile", TESTIMONIALS_VISUAL_DEFAULTS.cardStyle.paddingMobile, 0, 96),
      ...textStyleFields("testimonials.quoteStyle", "Testimonio", TESTIMONIALS_VISUAL_DEFAULTS.quoteStyle),
      ...textStyleFields("testimonials.nameStyle", "Nombre", TESTIMONIALS_VISUAL_DEFAULTS.nameStyle),
      ...textStyleFields("testimonials.roleStyle", "Referencia", TESTIMONIALS_VISUAL_DEFAULTS.roleStyle),
      ...textStyleFields("testimonials.accentStyle", "Comillas", TESTIMONIALS_VISUAL_DEFAULTS.accentStyle),
      visualColor("testimonials.avatarStyle.backgroundColor", "Avatar · fondo", TESTIMONIALS_VISUAL_DEFAULTS.avatarStyle.backgroundColor),
      visualColor("testimonials.avatarStyle.color", "Avatar · texto", TESTIMONIALS_VISUAL_DEFAULTS.avatarStyle.color),
      visualNumber("testimonials.avatarStyle.size", "Avatar · tamaño", TESTIMONIALS_VISUAL_DEFAULTS.avatarStyle.size, 24, 120),
      visualColor("testimonials.avatarStyle.borderColor", "Avatar · borde", TESTIMONIALS_VISUAL_DEFAULTS.avatarStyle.borderColor),
      visualNumber("testimonials.avatarStyle.borderWidth", "Avatar · grosor borde", TESTIMONIALS_VISUAL_DEFAULTS.avatarStyle.borderWidth, 0, 16),
      visualText("testimonials.avatarStyle.boxShadow", "Avatar · sombra", TESTIMONIALS_VISUAL_DEFAULTS.avatarStyle.boxShadow),
      visualStyleProperty(SEGMENT_TYPES.TESTIMONIALS),
    ],
  }),
  [SEGMENT_TYPES.CONTACT]: commonSegment({
    id: SEGMENT_TYPES.CONTACT,
    visibilityPath: "sections.showContact",
    elementEditingReady: true,
    elementTargets: {
      "contact.detailsCtaLabel": {
        kind: "button",
        label: "Botón de detalles",
        labelPath: "contact.detailsCtaLabel",
        urlPath: "contact.detailsCtaUrl",
        stylePrefix: "contact.detailsCtaStyle.",
      },
      "contact.whatsappCtaLabel": {
        kind: "button",
        label: "Botón de WhatsApp",
        labelPath: "contact.whatsappCtaLabel",
        extraPaths: ["business.whatsapp", "contact.whatsappMessage"],
        chips: ["WhatsApp"],
      },
      "@contact.business-data": {
        kind: "contact-group",
        editorMode: "business",
        label: "Datos del negocio",
        fieldPaths: [
          "business.phone",
          "business.email",
          "business.address",
          "business.city",
          "business.province",
          "business.country",
          "business.schedule",
        ],
      },
      "@contact.socials": {
        kind: "contact-group",
        editorMode: "socials",
        label: "Redes sociales",
        fieldPaths: [
          "business.socials.instagram",
          "business.socials.facebook",
          "business.socials.linkedin",
          "business.socials.website",
        ],
      },
      "@contact.map": {
        kind: "contact-group",
        editorMode: "map",
        label: "Mapa e imagen",
        imagePath: "contact.backgroundImageUrl",
        fieldPaths: ["business.mapEmbedUrl", "contact.mapTitle"],
      },
    },
    shellPropertyPaths: [
      "contact.backgroundColor",
      "contact.imageBackgroundColor",
      "contact.decorativeAccentColor",
      "contact.addressTextColor",
      "contact.scheduleTextColor",
      "contact.phoneTextColor",
      "contact.emailTextColor",
      "contact.addressIconColor",
      "contact.scheduleIconColor",
      "contact.phoneIconColor",
      "contact.emailIconColor",
    ],
    shellVisualBackground: false,
    shellVisualPalette: false,
    properties: [
      field("sections.showContact", SEGMENT_FIELD_TYPES.BOOLEAN, {
        label: "Mostrar contacto",
      }),
      field("contact.title", SEGMENT_FIELD_TYPES.TEXT, {
        label: "Título",
        colorPath: "contact.titleColor",
        maxLength: 220,
      }),
      field("contact.subtitle", SEGMENT_FIELD_TYPES.TEXTAREA, {
        label: "Texto de apoyo",
        colorPath: "contact.subtitleColor",
        maxLength: 900,
      }),
      field("contact.whatsappMessage", SEGMENT_FIELD_TYPES.TEXTAREA, {
        label: "Mensaje inicial de WhatsApp",
        maxLength: 500,
      }),
      field("contact.backgroundImageUrl", SEGMENT_FIELD_TYPES.IMAGE, {
        label: "Imagen de contacto",
        maxLength: URL_MAX_LENGTH,
        positionPath: "contact.backgroundImagePosition",
      }),
      field("contact.backgroundImagePosition", SEGMENT_FIELD_TYPES.SELECT, {
        label: "Posición de la imagen",
        options: IMAGE_POSITION_OPTIONS,
      }),
      field("contact.detailsCtaLabel", SEGMENT_FIELD_TYPES.TEXT, {
        label: "Botón de detalles",
        maxLength: 100,
      }),
      field("contact.detailsCtaUrl", SEGMENT_FIELD_TYPES.URL, {
        label: "Destino del botón de detalles",
        maxLength: URL_MAX_LENGTH,
        destinationPicker: true,
      }),
      field("contact.whatsappCtaLabel", SEGMENT_FIELD_TYPES.TEXT, {
        label: "Botón de WhatsApp",
        maxLength: 100,
      }),
      field("contact.mapTitle", SEGMENT_FIELD_TYPES.TEXT, {
        label: "Título del mapa",
        maxLength: 180,
      }),
      field("business.whatsapp", SEGMENT_FIELD_TYPES.TEXT, {
        label: "Número de WhatsApp",
        maxLength: 40,
        shared: true,
      }),
      field("business.phone", SEGMENT_FIELD_TYPES.TEXT, {
        label: "Teléfono",
        maxLength: 80,
        shared: true,
      }),
      field("business.email", SEGMENT_FIELD_TYPES.EMAIL, {
        label: "Correo electrónico",
        maxLength: 180,
        shared: true,
      }),
      field("business.address", SEGMENT_FIELD_TYPES.TEXT, {
        label: "Dirección",
        maxLength: 260,
        shared: true,
      }),
      field("business.city", SEGMENT_FIELD_TYPES.TEXT, {
        label: "Ciudad",
        maxLength: 120,
        shared: true,
      }),
      field("business.province", SEGMENT_FIELD_TYPES.TEXT, {
        label: "Provincia",
        maxLength: 120,
        shared: true,
      }),
      field("business.country", SEGMENT_FIELD_TYPES.TEXT, {
        label: "País",
        maxLength: 120,
        shared: true,
      }),
      field("business.schedule", SEGMENT_FIELD_TYPES.TEXTAREA, {
        label: "Horarios",
        maxLength: 400,
        shared: true,
      }),
      field("business.mapEmbedUrl", SEGMENT_FIELD_TYPES.URL, {
        label: "Mapa embebido",
        maxLength: URL_MAX_LENGTH,
        shared: true,
        validator: "map-embed",
      }),
      field("business.socials.instagram", SEGMENT_FIELD_TYPES.TEXT, {
        label: "Instagram",
        maxLength: URL_MAX_LENGTH,
        shared: true,
      }),
      field("business.socials.facebook", SEGMENT_FIELD_TYPES.TEXT, {
        label: "Facebook",
        maxLength: URL_MAX_LENGTH,
        shared: true,
      }),
      field("business.socials.linkedin", SEGMENT_FIELD_TYPES.TEXT, {
        label: "LinkedIn",
        maxLength: URL_MAX_LENGTH,
        shared: true,
      }),
      field("business.socials.website", SEGMENT_FIELD_TYPES.TEXT, {
        label: "Sitio externo",
        maxLength: URL_MAX_LENGTH,
        shared: true,
      }),
      field("contact.backgroundColor", SEGMENT_FIELD_TYPES.COLOR, { label: "Fondo de la sección", defaultValue: "#19242F", explicitVisual: true, maxLength: 100 }),
      field("contact.imageBackgroundColor", SEGMENT_FIELD_TYPES.COLOR, { label: "Fondo de la imagen", defaultValue: "rgba(255,255,255,.08)", explicitVisual: true, maxLength: 100 }),
      field("contact.decorativeAccentColor", SEGMENT_FIELD_TYPES.COLOR, { label: "Acento decorativo", defaultValue: "#F35A3A", explicitVisual: true, maxLength: 100 }),
      field("contact.titleColor", SEGMENT_FIELD_TYPES.COLOR, { label: "Color del título", defaultValue: "#FFFFFF", explicitVisual: true, maxLength: 100 }),
      field("contact.subtitleColor", SEGMENT_FIELD_TYPES.COLOR, { label: "Color del subtítulo", defaultValue: "rgba(255,255,255,.82)", explicitVisual: true, maxLength: 100 }),
      field("contact.addressTextColor", SEGMENT_FIELD_TYPES.COLOR, { label: "Color de dirección", defaultValue: "rgba(255,255,255,.82)", explicitVisual: true, maxLength: 100 }),
      field("contact.scheduleTextColor", SEGMENT_FIELD_TYPES.COLOR, { label: "Color de horarios", defaultValue: "rgba(255,255,255,.82)", explicitVisual: true, maxLength: 100 }),
      field("contact.phoneTextColor", SEGMENT_FIELD_TYPES.COLOR, { label: "Color de teléfono", defaultValue: "rgba(255,255,255,.82)", explicitVisual: true, maxLength: 100 }),
      field("contact.emailTextColor", SEGMENT_FIELD_TYPES.COLOR, { label: "Color de email", defaultValue: "rgba(255,255,255,.82)", explicitVisual: true, maxLength: 100 }),
      field("contact.addressIconColor", SEGMENT_FIELD_TYPES.COLOR, { label: "Color del ícono de dirección", defaultValue: "#F35A3A", explicitVisual: true, maxLength: 100 }),
      field("contact.scheduleIconColor", SEGMENT_FIELD_TYPES.COLOR, { label: "Color del ícono de horarios", defaultValue: "#F35A3A", explicitVisual: true, maxLength: 100 }),
      field("contact.phoneIconColor", SEGMENT_FIELD_TYPES.COLOR, { label: "Color del ícono de teléfono", defaultValue: "#F35A3A", explicitVisual: true, maxLength: 100 }),
      field("contact.emailIconColor", SEGMENT_FIELD_TYPES.COLOR, { label: "Color del ícono de email", defaultValue: "#F35A3A", explicitVisual: true, maxLength: 100 }),
      field("contact.detailsCtaStyle.textColor", SEGMENT_FIELD_TYPES.COLOR, { label: "Texto del botón de detalles", defaultValue: "#FFFFFF", explicitVisual: true, maxLength: 100 }),
      visualStyleProperty(SEGMENT_TYPES.CONTACT),
    ],
  }),
};

export default contentSegments;
